import store from "@/store/index"
import networkConnectivityConfig from "@/configs/networkConnectivityConfig.js"

let intervalId = null
let slowStreak = 0
let probeInFlight = false
let monitoringActive = false
let connectionChangeHandler = null
let visibilityChangeHandler = null
let activeProbeAbortController = null

const isDocumentHidden = () =>
  typeof document !== "undefined" && document.hidden === true

const stopProbeInterval = () => {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}

const abortActiveProbe = () => {
  activeProbeAbortController?.abort()
  activeProbeAbortController = null
}

const ensureProbeIntervalRunning = () => {
  if (
    !monitoringActive ||
    !networkConnectivityConfig.probeEnabled ||
    isDocumentHidden()
  ) {
    return
  }
  if (intervalId === null) {
    intervalId = window.setInterval(
      runLatencyProbe,
      networkConnectivityConfig.probeIntervalMs
    )
  }
}

const onDocumentVisibilityChange = () => {
  if (!monitoringActive) {
    return
  }
  if (isDocumentHidden()) {
    stopProbeInterval()
    abortActiveProbe()
    return
  }
  onConnectionInformationChange()
  ensureProbeIntervalRunning()
}

const attachDocumentVisibilityListener = () => {
  if (typeof document === "undefined" || visibilityChangeHandler) {
    return
  }
  visibilityChangeHandler = onDocumentVisibilityChange
  document.addEventListener("visibilitychange", visibilityChangeHandler)
}

const detachDocumentVisibilityListener = () => {
  if (typeof document === "undefined" || !visibilityChangeHandler) {
    return
  }
  document.removeEventListener("visibilitychange", visibilityChangeHandler)
  visibilityChangeHandler = null
}

const isSlowMonitoringAllowed = () =>
  networkConnectivityConfig.speedStatusEnabled &&
  networkConnectivityConfig.slowDetectionEnabled

export const getNetworkInformation = () => {
  if (typeof navigator === "undefined") {
    return null
  }
  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  )
}

export const isSlowPerNetworkInformation = () => {
  if (!isSlowMonitoringAllowed()) {
    return false
  }

  const conn = getNetworkInformation()
  if (!conn) {
    return false
  }

  const { slowEffectiveTypes, slowRttMs, slowDownlinkMbpsMax } =
    networkConnectivityConfig

  if (
    conn.effectiveType &&
    slowEffectiveTypes.includes(String(conn.effectiveType).toLowerCase())
  ) {
    return true
  }
  if (typeof conn.rtt === "number" && conn.rtt >= slowRttMs) {
    return true
  }
  if (
    typeof conn.downlink === "number" &&
    conn.downlink > 0 &&
    conn.downlink < slowDownlinkMbpsMax
  ) {
    return true
  }
  return false
}

const applySlowFromInformation = () => {
  if (isSlowPerNetworkInformation()) {
    store.dispatch("networkConnectivity/setSlowConnection", true)
    return true
  }
  return false
}

const runLatencyProbe = async () => {
  if (
    !monitoringActive ||
    probeInFlight ||
    !networkConnectivityConfig.probeEnabled ||
    isDocumentHidden()
  ) {
    return
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    store.dispatch("networkConnectivity/setSlowConnection", false)
    return
  }

  if (applySlowFromInformation()) {
    return
  }

  const { probeUrl, probeSlowMs, probeSlowStreakRequired, probeTimeoutMs } =
    networkConnectivityConfig

  probeInFlight = true
  const startedAt = performance.now()
  const cacheBust = startedAt.toFixed(0)

  const abortController = new AbortController()
  activeProbeAbortController = abortController
  const timeoutId = setTimeout(() => {
    abortController.abort()
  }, probeTimeoutMs)

  try {
    const response = await fetch(`${probeUrl}?nc=${cacheBust}`, {
      method: "GET",
      cache: "no-store",
      signal: abortController.signal
    })

    if (!monitoringActive) {
      return
    }

    if (!response.ok) {
      slowStreak = 0
      return
    }

    await response.arrayBuffer()

    if (!monitoringActive) {
      return
    }

    const elapsed = performance.now() - startedAt

    if (elapsed >= probeSlowMs) {
      slowStreak += 1
      if (slowStreak >= probeSlowStreakRequired) {
        store.dispatch("networkConnectivity/setSlowConnection", true)
      }
    } else {
      slowStreak = 0
      store.dispatch("networkConnectivity/setSlowConnection", false)
    }
  } catch {
    if (
      !monitoringActive ||
      isDocumentHidden() ||
      activeProbeAbortController !== abortController
    ) {
      return
    }
    slowStreak += 1
    if (slowStreak >= probeSlowStreakRequired) {
      store.dispatch("networkConnectivity/setSlowConnection", true)
    }
  } finally {
    clearTimeout(timeoutId)
    if (activeProbeAbortController === abortController) {
      activeProbeAbortController = null
    }
    probeInFlight = false
  }
}

const onConnectionInformationChange = () => {
  if (!monitoringActive) {
    return
  }
  if (applySlowFromInformation()) {
    return
  }
  if (networkConnectivityConfig.probeEnabled) {
    runLatencyProbe()
  } else {
    store.dispatch("networkConnectivity/setSlowConnection", false)
  }
}

export const startNetworkLatencyMonitoring = () => {
  if (typeof window === "undefined" || !isSlowMonitoringAllowed()) {
    return
  }

  monitoringActive = true

  attachDocumentVisibilityListener()

  const conn = getNetworkInformation()
  if (conn && !connectionChangeHandler) {
    connectionChangeHandler = onConnectionInformationChange
    conn.addEventListener("change", connectionChangeHandler)
  }

  applySlowFromInformation()

  if (networkConnectivityConfig.probeEnabled && !isDocumentHidden()) {
    onConnectionInformationChange()
    ensureProbeIntervalRunning()
  }
}

export const stopNetworkLatencyMonitoring = () => {
  monitoringActive = false
  slowStreak = 0
  abortActiveProbe()
  stopProbeInterval()
  detachDocumentVisibilityListener()

  const conn = getNetworkInformation()
  if (conn && connectionChangeHandler) {
    conn.removeEventListener("change", connectionChangeHandler)
    connectionChangeHandler = null
  }

  store.dispatch("networkConnectivity/setSlowConnection", false)
}

/** Re-run slow detection after browser reports online (e.g. DevTools offline → throttled). */
export const refreshNetworkSlowConnectionState = () => {
  if (!monitoringActive || !isSlowMonitoringAllowed()) {
    return
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return
  }
  if (isDocumentHidden()) {
    return
  }
  onConnectionInformationChange()
  ensureProbeIntervalRunning()
}
